import { Builder, Input } from '@builder.io/sdk';
import { PageHero } from './PageHero';
import { BuilderInputOptions } from '@/types/builder';
import { CONTENT_TYPES } from './PageHero.types';
import { getFilteredFormInputs } from '../LandingPageForm/LandingPageForm.registry';

const getFormInputs = () =>
  getFilteredFormInputs({
    excludedFields: ['heading', 'subHeading', 'useCardStyle'],
    defaultOverrides: {
      regLabel: 'Enter Registration Number',
    },
    gateOnEnableForm: true,
  });

const ProductPageHeaderConfig = {
  name: 'PageHero',
  friendlyName: 'Page Hero',
  inputs: [
    {
      name: 'headingOnly',
      type: 'boolean',
      required: false,
      defaultValue: false,
      helperText: 'When enabled, only the title is shown — no image, badge, subtitle, or form',
    },
    {
      name: 'badge',
      type: 'string',
      required: false,
      helperText: 'Small coloured label above the title (e.g. "Tempcover content hub"). Leave empty to hide.',
      showIf: (options: BuilderInputOptions) => !options?.get('headingOnly'),
    },
    {
      name: 'title',
      type: 'string',
      required: true,
      helperText: 'Main heading — displayed as the large bold title',
    },
    {
      name: 'subtitle',
      type: 'string',
      required: false,
      helperText: 'Bold intro text below the title (plain text, 1–3 sentences)',
      showIf: (options: BuilderInputOptions) => !options?.get('headingOnly'),
    },
    {
      name: 'tagline',
      type: 'richText',
      required: false,
      helperText: 'Rich text below the subtitle — use when you need links or formatting',
      showIf: (options: BuilderInputOptions) => !options?.get('headingOnly'),
    },
    {
      name: 'enableForm',
      type: 'boolean',
      required: false,
      defaultValue: false,
      helperText: 'Replace the hero image with a quote form',
      showIf: (options: BuilderInputOptions) => !options?.get('headingOnly'),
    },
    ...getFormInputs(),
    {
      name: 'contentType',
      type: 'string',
      enum: Object.values(CONTENT_TYPES),
      defaultValue: CONTENT_TYPES.LIST,
      required: true,
      helperText: 'List = tick-icon bullet points, Text = rich text block, Trustpilot = Trustpilot widget + text',
      showIf: (options: BuilderInputOptions) => !options?.get('headingOnly'),
    },
    {
      name: 'points',
      type: 'list',
      required: false,
      showIf: (options: BuilderInputOptions) => options?.get('contentType') === 'List',
      helperText: 'Each point is shown with a tick icon',
      subFields: [
        {
          name: 'point',
          type: 'string',
        },
      ],
    },
    {
      name: 'body',
      type: 'richText',
      required: false,
      showIf: (options: BuilderInputOptions) =>
        options?.get('contentType') === 'Text' || options?.get('contentType') === 'Trustpilot',
      helperText: 'Body text shown below the subtitle/tagline',
    },
    {
      name: 'image',
      type: 'file',
      required: false,
      helperText: 'Hero image displayed alongside the text content',
      showIf: (options: BuilderInputOptions) => !options?.get('enableForm'),
    },
    {
      name: 'imageFit',
      type: 'enum',
      enum: [
        { label: 'Fill area (crop if needed)', value: 'cover' },
        { label: 'Fit inside (no cropping)', value: 'contain' },
      ],
      required: false,
      defaultValue: 'cover',
      helperText: 'How the image fills its space',
      showIf: (options: BuilderInputOptions) => !options?.get('enableForm'),
    },
    {
      name: 'imagePosition',
      type: 'enum',
      enum: [
        { label: 'Left of text', value: 'left' },
        { label: 'Right of text', value: 'right' },
      ],
      required: false,
      defaultValue: 'right',
      helperText: 'Which side the image appears on (desktop only)',
      showIf: (options: BuilderInputOptions) => !options?.get('enableForm'),
    },
    {
      name: 'showAppLinks',
      type: 'boolean',
      required: false,
      defaultValue: false,
      helperText: 'Show App Store and Google Play download buttons',
      showIf: (options: BuilderInputOptions) => !options?.get('headingOnly'),
    },
    {
      name: 'appLinks',
      type: 'reference',
      model: 'store-links',
      helperText: 'Select the store links data to use',
      showIf: (options: BuilderInputOptions) => !!options?.get('showAppLinks'),
    },
  ] as Input[],
};

Builder.registerComponent(PageHero, ProductPageHeaderConfig);
